import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './Calendar.css';
import AddNoteModal from './AddNoteModal';
import { formatPhoneForDisplay, formatPhoneInput } from '../utils/phone';

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function addWeeks(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n * 7);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Helper to only show alerts in development
function devAlert(message) {
  if (process.env.NODE_ENV === 'development') {
    alert(message);
  }
}

function toLocalISOString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function generateTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const time = `${hh}:${mm}`;
      
      // Format for display
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      const label = `${h12}:${mm} ${ampm}`;
      
      options.push({ value: time, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

// Business hours (24-hour strings)
const BUSINESS_START = '08:00';
// We'll align modal/business limits to the display slot grid when workingHoursOnly is enabled
const DISPLAY_SLOT_COUNT = 22; // 22 * 30min = 11 hours (8AM - 7PM)
const SLOT_MINUTES = 30;
const BUSINESS_LAST_START = minutesToTime(timeToMinutes(BUSINESS_START) + (DISPLAY_SLOT_COUNT - 1) * SLOT_MINUTES); // last slot start
const BUSINESS_MAX_END = minutesToTime(timeToMinutes(BUSINESS_START) + DISPLAY_SLOT_COUNT * SLOT_MINUTES); // display end

// helper already exists above (timeToMinutes), but provide a small wrapper if needed
// (timeToMinutes and minutesToTime are defined earlier in this file)

function eventOverlapsBusinessHours(ev) {
  const s = timeToMinutes(ev.start_time || '00:00');
  const e = timeToMinutes(ev.end_time || ev.start_time || '00:00');
  const bs = timeToMinutes(BUSINESS_START);
  const be = timeToMinutes(BUSINESS_MAX_END);
  if (s === null || e === null) return true; // include undated events
  // overlap if event start is before business max end AND event end is after business start
  return s < be && e > bs;
}

function getLabelForTime(value) {
  const found = TIME_OPTIONS.find(o => o.value === value);
  if (found) return found.label;
  return formatTime(value);
}

function getStartOptions(currentStart) {
  const startMin = timeToMinutes(BUSINESS_START);
  const startMax = timeToMinutes(BUSINESS_LAST_START);
  const opts = TIME_OPTIONS.filter(o => {
    const m = timeToMinutes(o.value);
    return m >= startMin && m <= startMax;
  });
  // if currentStart is outside allowed range, include it so editing existing events still shows value
  if (currentStart && !opts.some(o => o.value === currentStart)) {
    opts.unshift({ value: currentStart, label: getLabelForTime(currentStart) });
  }
  return opts;
}

function getEndOptions(start) {
  const s = timeToMinutes(start || BUSINESS_START);
  const max = timeToMinutes(BUSINESS_MAX_END);
  const opts = TIME_OPTIONS.filter(o => {
    const m = timeToMinutes(o.value);
    return m > s && m <= max;
  });
  return opts;
}

// Helpers for time math
function timeToMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  mins = Math.max(0, Math.min(24 * 60 - 1, Math.round(mins)));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Ensure time strings are consistently HH:MM
function normalizeTime(t) {
  if (!t) return t;
  const parts = String(t).split(':');
  const h = parts[0] != null ? Number(parts[0]) : 0;
  const m = parts[1] != null ? Number(parts[1]) : 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function getNegotiatedPriceError(value) {
  const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '').trim();
  if (!cleaned) return 'Price is required.';
  const parsed = parseFloat(cleaned);
  if (Number.isNaN(parsed)) return 'Price must be a valid number.';
  if (parsed < 0) return 'Price cannot be negative.';
  return '';
}

function normalizeMoneyInput(value) {
  const raw = String(value ?? '');
  if (!raw) return '';

  let cleaned = '';
  let hasDot = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      cleaned += ch;
      continue;
    }
    if (ch === '.' && !hasDot) {
      cleaned += ch;
      hasDot = true;
    }
  }
  if (!cleaned) return '';

  const [intPartRaw, decimalPartRaw = ''] = cleaned.split('.');
  const integer = String(intPartRaw || '').replace(/^0+(?=\d)/, '');
  const decimals = decimalPartRaw.slice(0, 2);

  if (hasDot) {
    return `${integer || '0'}.${decimals}`;
  }
  return integer;
}

function formatMoneyOnBlur(value) {
  const normalized = normalizeMoneyInput(value);
  if (!normalized) return '';
  const parsed = Number(normalized);
  if (Number.isNaN(parsed) || parsed < 0) return '';
  return parsed.toFixed(2);
}

function parseMoneyToNumber(value) {
  const normalized = normalizeMoneyInput(value);
  if (!normalized) return NaN;
  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) return NaN;
  return Number(parsed.toFixed(2));
}

function handleMoneyMouseDown(e) {
  if (document.activeElement !== e.target) {
    e.preventDefault();
    e.target.focus();
    e.target.select();
  }
}

// Display mapping for vertical positioning inside a day cell (derived from BUSINESS_START and slots)
const DISPLAY_START = BUSINESS_START;
const DISPLAY_END = minutesToTime(timeToMinutes(DISPLAY_START) + DISPLAY_SLOT_COUNT * SLOT_MINUTES);

function computeEventPosition(ev) {
  const displayStartMin = timeToMinutes(DISPLAY_START);
  const displayEndMin = timeToMinutes(DISPLAY_END);
  const total = Math.max(1, displayEndMin - displayStartMin);
  const evStart = timeToMinutes(ev.start_time || DISPLAY_START);
  let evEnd = ev.end_time ? timeToMinutes(ev.end_time) : null;
  if (evEnd === null) evEnd = evStart + 30; // default 30 minutes duration when end not provided
  const startClamped = Math.max(displayStartMin, Math.min(displayEndMin, evStart));
  const endClamped = Math.max(displayStartMin, Math.min(displayEndMin, Math.max(evEnd, startClamped + SLOT_MINUTES)));
  const topPct = ((startClamped - displayStartMin) / total) * 100;
  const heightPct = ((endClamped - startClamped) / total) * 100;
  return { top: `${topPct}%`, height: `${Math.max(heightPct, 3)}%` };
}

function renderEventTitle(ev) {
  const title = ev.title || '';
  const paymentStatus = ev.payment_status; // 0=Unpaid, 2=Paid
  
  // Notes (job_status 5) should not have secondary status styling
  if (parseInt(ev.job_status, 10) === 5) return title;

  // Case-insensitive match for anything before "Acct is" and anything after
  const match = title.match(/(.*)\s+Acct\s+is\s+(.*)/i);
  
  if (match) {
    const mainPart = match[1];
    const statusLabel = paymentStatus === 2 ? 'Paid' : 'Unpaid';
    const statusColor = paymentStatus === 2 ? '#2ecc71' : '#e74c3c';

    return (
      <span>
        {mainPart} Acct is <strong style={{ color: statusColor, fontWeight: 'bold' }}>{statusLabel}</strong>
      </span>
    );
  }
  
  return title;
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function buildEventAddress(ev = {}) {
  const parts = [ev.addr_street, ev.addr_city, ev.addr_state, ev.addr_zip_code]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  return parts.join(', ');
}

function buildGoogleOptimizedRouteUrl(addresses = []) {
  const normalized = Array.from(new Set((addresses || []).map((a) => String(a || '').trim()).filter(Boolean)));
  if (normalized.length < 2) return '';

  const origin = normalized[0];
  const destination = normalized[normalized.length - 1];
  const waypointsCore = normalized.slice(1, -1);
  const params = new URLSearchParams({
    api: '1',
    travelmode: 'driving',
    origin,
    destination
  });

  if (waypointsCore.length > 0) {
    params.set('waypoints', `optimize:true|${waypointsCore.join('|')}`);
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function formatRouteDistance(meters) {
  const value = Number(meters || 0);
  if (!Number.isFinite(value) || value <= 0) return '-';
  const miles = value / 1609.344;
  return `${miles.toFixed(1)} mi`;
}

function formatRouteDuration(seconds) {
  const value = Number(seconds || 0);
  if (!Number.isFinite(value) || value <= 0) return '-';
  const hrs = Math.floor(value / 3600);
  const mins = Math.round((value % 3600) / 60);
  if (hrs <= 0) return `${mins} min`;
  if (mins <= 0) return `${hrs} hr`;
  return `${hrs} hr ${mins} min`;
}

function getAttachmentExtension(fileName = '') {
  const base = String(fileName || '').trim();
  const idx = base.lastIndexOf('.');
  if (idx <= -1 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

function isImageAttachment(att = {}) {
  const mime = String(att?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const ext = getAttachmentExtension(att?.original_name || att?.stored_name || '');
  return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext);
}

function getAttachmentTypeLabel(att = {}) {
  const ext = getAttachmentExtension(att?.original_name || att?.stored_name || '');
  if (ext) return ext.toUpperCase();
  const mime = String(att?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'IMAGE';
  if (mime.includes('pdf')) return 'PDF';
  return 'FILE';
}

// Removed footer meta rendering (Price/Payment) per latest requirements

function Calendar({ token, onLogout }) {
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [view, setView] = useState('month'); // 'month' | 'week' | 'day'
  const [selectedDay, setSelectedDay] = useState(today);

  const [workingHoursOnly, setWorkingHoursOnly] = useState(true);
  const isAdmin = (() => {
    try {
      const p = parseJwt(token);
      return p?.role === 'admin';
    } catch (e) { return false; }
  })();

  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function prev() {
    if (view === 'month') setCursor(addMonths(cursor, -1));
    else if (view === 'week') setCursor(addWeeks(cursor, -1));
    else setCursor(addDays(cursor, -1));
  }

  function next() {
    if (view === 'month') setCursor(addMonths(cursor, 1));
    else if (view === 'week') setCursor(addWeeks(cursor, 1));
    else setCursor(addDays(cursor, 1));
  }

  // For month view, compute weeks as before
  const weeks = [];
  if (view === 'month') {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const startDay = new Date(first);
    startDay.setDate(first.getDate() - first.getDay());

    let current = new Date(startDay);
    while (current <= last || current.getDay() !== 0) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
      if (current > last && current.getDay() === 0) break;
    }
  }

  // For week view, compute single week starting from startOfWeek(cursor)
  const weekDays = [];
  if (view === 'week') {
    const start = startOfWeek(cursor);
    for (let i = 0; i < 7; i++) {
      weekDays.push(addDays(start, i));
    }
  }

  // For day view, single day
  const day = view === 'day' ? cursor : null;
  const [showForm, setShowForm] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  // Default to 'appt' on load. Persisted value will no longer override initial default.
  const [mode, setMode] = useState('appt'); // 'appt' | 'note'
  const [noteEditData, setNoteEditData] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  // resizing removed — keep UI simple; click suppression not needed

  function showTemporaryToast(msg, ms = 3000) {
    if (process.env.NODE_ENV !== 'development') return; // Do not show toast in production
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      setToastMsg('');
    }, ms);
  }
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [jobCoas, setJobCoas] = useState([]);
  const [jobCoaId, setJobCoaId] = useState('');
  const [negotiatedPrice, setNegotiatedPrice] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerAddress1, setCustomerAddress1] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [customerZip, setCustomerZip] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(0);
  const [jobStatus, setJobStatus] = useState(0);
  const [notes, setNotes] = useState('');
  const [editingEventId, setEditingEventId] = useState(null);
  const [events, setEvents] = useState([]);
  const [cashAmt, setCashAmt] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [attachmentPreviews, setAttachmentPreviews] = useState({});
  const [attachmentPreviewLoading, setAttachmentPreviewLoading] = useState({});
  const [showAttachmentComposer, setShowAttachmentComposer] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeSummary, setRouteSummary] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [deleteEventError, setDeleteEventError] = useState('');
  const submitInFlightRef = useRef(false);
  const isNoteMode = String(jobStatus) === '5';
  const negotiatedPriceError = isNoteMode ? '' : getNegotiatedPriceError(negotiatedPrice);
  const callerRole = (() => {
    try {
      const payload = parseJwt(token);
      return String(payload?.role || '').trim().toLowerCase();
    } catch (_err) {
      return '';
    }
  })();
  // Dormant feature switches: logic remains in code, but UI access stays hidden.
  const canUseAttachments = false;
  const canUseRouteOptimization = false;
  const canUseRouteSummary = false;
  const canEditAttachments = canUseAttachments && callerRole === 'office';

  // Auto-generate title
  useEffect(() => {
    if (!showForm || jobStatus === '5') return;
    
    const jobType = jobCoas.find(c => c.coa_id.toString() === jobCoaId.toString())?.description || 'Unknown Job';
    const parsedTitlePrice = parseMoneyToNumber(negotiatedPrice);
    const price = Number.isNaN(parsedTitlePrice) ? '0.00' : parsedTitlePrice.toFixed(2);
    
    const paymentStatusMap = { 0: 'Unpaid', 2: 'Paid' };
    
    const pStatus = paymentStatusMap[paymentStatus] || 'Unpaid';
    
    const formattedPhone = customerPhone ? formatPhoneForDisplay(customerPhone) : '';
    const phoneStr = formattedPhone ? ` ${formattedPhone}` : '';
    const addressStr = customerAddress1 ? ` ${customerAddress1}` : '';
    const zipStr = customerZip ? ` ${customerZip}` : '';
    
    const newTitle = `${jobType} - $${price} ${customerLastName}${phoneStr}${addressStr}${zipStr} Acct is ${pStatus}`;
    setTitle(newTitle);
  }, [jobCoaId, negotiatedPrice, paymentStatus, jobCoas, showForm, customerLastName, customerPhone, customerAddress1, customerZip, jobStatus]);

  useEffect(() => {
    if (!showForm || jobStatus === '5') return;
    if (!jobCoaId) return;
    if (String(negotiatedPrice ?? '').trim() !== '') return;

    const selectedCoa = jobCoas.find(coa => String(coa.coa_id) === String(jobCoaId));
    if (selectedCoa && selectedCoa.price != null && selectedCoa.price !== '') {
      setNegotiatedPrice(formatMoneyOnBlur(selectedCoa.price));
    }
  }, [showForm, jobStatus, jobCoaId, negotiatedPrice, jobCoas]);

  // Customer search timer
  useEffect(() => {
    if (customerSearch.trim() === '') {
      setCustomerResults([]);
      return;
    }
    const timer = setTimeout(() => {
      axios.get(`/customers/search?q=${customerSearch}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCustomerResults(res.data))
        .catch(err => console.error('Customer search failed', err));
    }, 300); // Debounce API call
    return () => clearTimeout(timer);
  }, [customerSearch, token]);

  // Close modal on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && showForm) setShowForm(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  useEffect(() => {
    if (token) {
      loadEvents();
      loadJobCoas();
      
      // Auto-refresh every 30 seconds to update payment statuses from webhooks
      const interval = setInterval(loadEvents, 30000);
      return () => clearInterval(interval);
    } else {
      setEvents([]);
      setJobCoas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load persisted UI preference for showing all hours
  useEffect(() => {
    try {
      const v = localStorage.getItem('calendar_working_hours_only');
      if (v !== null) setWorkingHoursOnly(JSON.parse(v));
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (showForm) return;
    setAttachmentPreviewLoading({});
    setAttachmentPreviews((prev) => {
      Object.values(prev).forEach((url) => {
        if (url) window.URL.revokeObjectURL(url);
      });
      return {};
    });
  }, [showForm]);

  async function loadEvents() {
    try {
      const res = await axios.get(`/events`, { headers: { Authorization: `Bearer ${token}` } });
      const normalized = (res.data.events || []).map(ev => ({
        ...ev,
        start_time: normalizeTime(ev.start_time),
        end_time: normalizeTime(ev.end_time)
      }));
      setEvents(normalized);
    } catch (err) {
      console.error('Failed to load events', err?.response || err);
      if (err.response && err.response.status === 401 && onLogout) {
        onLogout();
      }
      setEvents([]);
    }
  }

  async function loadJobCoas() {
    try {
      const res = await axios.get(`/job-coas`, { headers: { Authorization: `Bearer ${token}` } });
      console.log('Loaded Job COAs:', res.data);
      setJobCoas(res.data || []);
    } catch (err) {
      console.error('Failed to load Job COAs', err?.response || err);
      if (err.response && err.response.status === 401 && onLogout) {
        onLogout();
      }
    }
  }

  function handleStartTimeChange(e) {
    const newStart = e.target.value;
    setStartTime(newStart);
    if (newStart) {
      const s = timeToMinutes(newStart);
      const target = Math.min(s + 120, timeToMinutes(BUSINESS_MAX_END));
      setEndTime(minutesToTime(target));
    }
  }

  function resetForm() {
    setTitle(''); setDescription('');
    setStartTime('09:00'); setEndTime('10:00');
    setSelectedCustomer(null); setCustomerSearch(''); setCustomerResults([]);
    setJobCoaId(''); setNegotiatedPrice(''); 
    setCustomerFirstName(''); setCustomerLastName(''); setCustomerEmail(''); setCustomerPhone(''); setCustomerAddress1(''); setCustomerCity(''); setCustomerState(''); setCustomerZip('');
    setPaymentStatus(0); setJobStatus(0); setNotes('');
    setEditingEventId(null);
    setCashAmt('');
    setAttachments([]);
    setAttachmentFile(null);
    setAttachmentError('');
    setShowAttachmentComposer(false);
    setAttachmentPreviewLoading({});
    setAttachmentPreviews((prev) => {
      Object.values(prev).forEach((url) => {
        if (url) window.URL.revokeObjectURL(url);
      });
      return {};
    });
    setDeleteEventError('');
  }

  async function loadAttachments(eventId) {
    if (!canUseAttachments) {
      setAttachments([]);
      return;
    }
    if (!eventId) {
      setAttachments([]);
      return;
    }

    try {
      setAttachmentsLoading(true);
      setAttachmentError('');
      const res = await axios.get(`/events/${eventId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttachments(res.data?.attachments || []);
    } catch (err) {
      console.error('Failed to load attachments', err);
      setAttachments([]);
      setAttachmentError('Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  }

  async function handleUploadAttachment() {
    if (!canUseAttachments) {
      setAttachmentError('Attachments are disabled for this tenant plan.');
      return;
    }
    if (!canEditAttachments) {
      setAttachmentError('Attachment upload is restricted to office users.');
      return;
    }
    if (!editingEventId || !attachmentFile) return;

    try {
      setAttachmentUploading(true);
      setAttachmentError('');

      await axios.post(`/events/${editingEventId}/attachments`, attachmentFile, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': attachmentFile.type || 'application/octet-stream',
          'x-file-name': attachmentFile.name
        }
      });

      setAttachmentFile(null);
      await loadAttachments(editingEventId);
    } catch (err) {
      console.error('Failed to upload attachment', err);
      setAttachmentError(err?.response?.data?.error || 'Failed to upload attachment');
    } finally {
      setAttachmentUploading(false);
    }
  }

  async function handleDeleteAttachment(attachmentId) {
    if (!canUseAttachments) {
      setAttachmentError('Attachments are disabled for this tenant plan.');
      return;
    }
    if (!canEditAttachments) {
      setAttachmentError('Attachment delete is restricted to office users.');
      return;
    }
    if (!editingEventId || !attachmentId) return;
    if (!window.confirm('Delete this attachment?')) return;

    try {
      setAttachmentError('');
      await axios.delete(`/events/${editingEventId}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadAttachments(editingEventId);
    } catch (err) {
      console.error('Failed to delete attachment', err);
      setAttachmentError(err?.response?.data?.error || 'Failed to delete attachment');
    }
  }

  async function handleDeleteEvent() {
    if (!editingEventId || deletingEvent || savingEvent) return;
    const isPaid = Number(paymentStatus) === 2;
    const isCompleted = Number(jobStatus) === 2;
    if (isPaid || isCompleted) {
      setDeleteEventError('Paid or completed events cannot be deleted. Edit the event first and set Payment to Unpaid and Job Status to Scheduled, save, then delete it.');
      return;
    }

    setDeleteEventError('');
    const ok = window.confirm('Delete this event? This cannot be undone.');
    if (!ok) return;

    try {
      setDeletingEvent(true);
      await axios.delete(`/events/${editingEventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowForm(false);
      resetForm();
      await loadEvents();
      devAlert('Event deleted');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to delete event';
      setDeleteEventError(msg);
      devAlert(msg);
    } finally {
      setDeletingEvent(false);
    }
  }

  async function handleDownloadAttachment(attachment) {
    if (!canUseAttachments) {
      setAttachmentError('Attachments are disabled for this tenant plan.');
      return;
    }
    if (!editingEventId || !attachment?.id) return;

    try {
      const response = await axios.get(
        `/events/${editingEventId}/attachments/${attachment.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: attachment.mime_type || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_name || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download attachment', err);
      setAttachmentError(err?.response?.data?.error || 'Failed to download attachment');
    }
  }

  async function handleToggleAttachmentPreview(attachment) {
    if (!canUseAttachments) return;
    if (!editingEventId || !attachment?.id || !isImageAttachment(attachment)) return;

    const existingPreview = attachmentPreviews[attachment.id];
    if (existingPreview) {
      window.URL.revokeObjectURL(existingPreview);
      setAttachmentPreviews((prev) => {
        const next = { ...prev };
        delete next[attachment.id];
        return next;
      });
      return;
    }

    try {
      setAttachmentPreviewLoading((prev) => ({ ...prev, [attachment.id]: true }));
      setAttachmentError('');
      const response = await axios.get(
        `/events/${editingEventId}/attachments/${attachment.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: attachment.mime_type || 'application/octet-stream' }));
      setAttachmentPreviews((prev) => ({ ...prev, [attachment.id]: objectUrl }));
    } catch (err) {
      console.error('Failed to preview attachment', err);
      setAttachmentError(err?.response?.data?.error || 'Failed to preview attachment');
    } finally {
      setAttachmentPreviewLoading((prev) => {
        const next = { ...prev };
        delete next[attachment.id];
        return next;
      });
    }
  }

  function getRouteStopsForDate(routeDate) {
    return events
      .filter((ev) => ev.date === routeDate)
      .filter((ev) => Number(ev.job_status) !== 5)
      .filter((ev) => Number(ev.job_status) !== 3)
      .filter((ev) => !workingHoursOnly || eventOverlapsBusinessHours(ev))
      .sort((a, b) => (a.start_time || '00:00').localeCompare(b.start_time || '00:00'))
      .map((ev, idx) => {
        const address = buildEventAddress(ev);
        if (!address) return null;
        const titlePart = String(ev.title || '').trim() || `Stop ${idx + 1}`;
        return {
          id: ev.id,
          label: `${ev.start_time || '--:--'} ${titlePart}`,
          address,
          startTime: ev.start_time || null
        };
      })
      .filter(Boolean);
  }

  async function handleOptimizeRoute() {
    if (!canUseRouteOptimization) {
      setRouteError('Route optimization is disabled for this tenant plan.');
      return;
    }
    const routeDate = view === 'day' ? toLocalISOString(cursor) : toLocalISOString(selectedDay || cursor);
    const stops = getRouteStopsForDate(routeDate);

    if (stops.length < 2) {
      setRouteError('Need at least 2 events with valid addresses to optimize route.');
      setRouteSummary(null);
      return;
    }

    try {
      setRouteLoading(true);
      setRouteError('');

      const response = await axios.post(
        '/routes/optimize',
        { stops },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payload = response?.data || {};
      setRouteSummary({
        date: routeDate,
        orderedStops: Array.isArray(payload.orderedStops) ? payload.orderedStops : stops,
        totalDistanceMeters: payload.totalDistanceMeters,
        totalDurationSeconds: payload.totalDurationSeconds,
        mapUrl: payload.mapUrl || buildGoogleOptimizedRouteUrl(stops.map((s) => s.address)),
        provider: payload.provider || 'unknown'
      });

      if (payload.warning) {
        setRouteError(payload.warning);
      }

      if (payload.mapUrl) {
        window.open(payload.mapUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to optimize route', err);
      setRouteSummary(null);
      setRouteError(err?.response?.data?.error || 'Failed to optimize route.');
    } finally {
      setRouteLoading(false);
    }
  }

  const routeDateLabel = view === 'day' ? toLocalISOString(cursor) : toLocalISOString(selectedDay || cursor);
  const routeCandidateCount = events
    .filter((ev) => ev.date === routeDateLabel)
    .filter((ev) => Number(ev.job_status) !== 5)
    .map(buildEventAddress)
    .filter(Boolean)
    .length;
  const showRouteSummary = Boolean(canUseRouteSummary && routeSummary && routeSummary.date === routeDateLabel);

  // Resizing feature removed to simplify UX

  // Resizing logic removed

  function handleEditEvent(ev) {
    // If this is a Note event, open the Note modal instead of the appointment modal
    if (parseInt(ev.job_status, 10) === 5) {
      setNoteEditData({
        id: ev.id,
        date: ev.date,
        start_time: ev.start_time || '09:00',
        end_time: ev.end_time || '10:00',
        title: ev.title || '',
        notes: ev.notes || ''
      });
      setShowAddNoteModal(true);
      return;
    }

    setEditingEventId(ev.id);
    // Fix: Update selectedDay to the event's date so saving preserves the date
    if (ev.date) {
      const [y, m, d] = ev.date.split('-').map(Number);
      setSelectedDay(new Date(y, m - 1, d));
    }

    setTitle(ev.title);
    setDescription(ev.description || '');
    setStartTime(normalizeTime(ev.start_time) || '09:00');
    setEndTime(normalizeTime(ev.end_time) || '10:00');
    // Ensure values are strings for select inputs
    setJobCoaId(ev.job_number ? String(ev.job_number) : '');
    setNegotiatedPrice(formatMoneyOnBlur(ev.negotiated_price));
    setPaymentStatus(String(ev.payment_status ?? 0));
    setJobStatus(String(ev.job_status ?? 0));
    setNotes(ev.notes || '');
    setCashAmt(ev.cash_amount != null ? String(ev.cash_amount) : '');
    setAttachmentFile(null);
    setAttachmentError('');
    
    if (ev.customer_id) {
      setSelectedCustomer({
        cust_id: ev.customer_id,
        first_name: ev.first_name,
        last_name: ev.last_name,
        email: ev.customer_email,
        phone_number: ev.phone_number,
        addr_street: ev.addr_street,
        addr_city: ev.addr_city,
        addr_state: ev.addr_state,
        addr_zip_code: ev.addr_zip_code
      });
      setCustomerFirstName(ev.first_name || '');
      setCustomerLastName(ev.last_name || '');
      setCustomerEmail(ev.customer_email || '');
      setCustomerPhone(formatPhoneInput(ev.phone_number || ''));
      setCustomerAddress1(ev.addr_street || '');
      setCustomerCity(ev.addr_city || '');
      setCustomerState(ev.addr_state || '');
      setCustomerZip(ev.addr_zip_code || '');
    } else {
      setSelectedCustomer(null);
      setCustomerFirstName('');
      setCustomerLastName('');
      setCustomerEmail('');
      setCustomerPhone('');
      setCustomerAddress1('');
      setCustomerCity('');
      setCustomerState('');
      setCustomerZip('');
    }
    
    setShowForm(true);
    loadAttachments(ev.id);
  }

  function openCreateForDay(dayObj) {
    setCursor(new Date(dayObj));
    setSelectedDay(new Date(dayObj));
    resetForm();
    if (mode === 'note') {
      setShowAddNoteModal(true);
    } else {
      setShowForm(true);
    }
  }

  function handleDragStart(e, ev) {
    e.dataTransfer.setData('eventId', ev.id);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  async function handleDrop(e, targetDay) {
    e.preventDefault();
    const eventId = e.dataTransfer.getData('eventId');
    if (!eventId) return;

    const targetDateStr = toLocalISOString(targetDay);
    const event = events.find(ev => ev.id === eventId);
    if (!event) return;

    // If date hasn't changed, do nothing
    if (event.date === targetDateStr) return;

    try {
      const payload = {
        date: targetDateStr,
        start_time: event.start_time,
        end_time: event.end_time,
        title: event.title,
        description: event.description || '',
        customer_id: event.customer_id,
        job_coa_id: event.job_coa_id,
        job_number: event.job_number,
        negotiated_price: parseFloat(event.negotiated_price),
        payment_status: parseInt(event.payment_status, 10),
        job_status: parseInt(event.job_status, 10),
        notes: event.notes || ''
      };

      await axios.put(`/events/${eventId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await loadEvents();
    } catch (err) {
      console.error('Failed to move event', err);
      devAlert('Failed to move event');
    }
  }

  async function handleSendInvoice() {
    if (!editingEventId) return;
    if (!customerEmail) return alert('Customer needs an email address to send an invoice.');
    
    const cashValue = parseFloat(cashAmt) || 0;
    const totalDue = parseFloat(negotiatedPrice) || 0;

    if (cashValue > totalDue) {
      return alert('Cash amount cannot be greater than the total amount due.');
    }

    const remainingBalance = totalDue - cashValue;

    let confirmMessage = `Send payment request to ${customerEmail} for $${negotiatedPrice}?`;
    if (cashValue > 0) {
      confirmMessage = `Total: $${totalDue.toFixed(2)}\nCash Paid: $${cashValue.toFixed(2)}\nAmount to Charge: $${remainingBalance.toFixed(2)}\n\nSend payment request to ${customerEmail}?`;
    }
    
    if (!window.confirm(confirmMessage)) return;

    try {
      await axios.post(`/events/${editingEventId}/invoice`, { cashAmt: cashValue }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Payment request sent. Invoice will email after successful payment.');
      await submitEvent({ preventDefault: () => {} }, day ? day : selectedDay);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to send invoice');
    }
  }

  async function submitEvent(e, targetDate) {
    e.preventDefault();
    if (submitInFlightRef.current) return;
    const target = targetDate || (day ? day : selectedDay);
    
    // Validate required fields
    const isNote = jobStatus === '5';
    if (!title || !startTime || !endTime || !target || (!isNote && !jobCoaId)) {
      return devAlert('Missing required fields (Title, Time, Job Type).');
    }

    // Validate customer: either selected or new name provided (skip for notes)
    if (!isNote && !selectedCustomer && (!customerFirstName || !customerLastName)) {
      return devAlert('Please select a customer or enter a First and Last Name.');
    }

    // Validate email format if provided
    if (customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      return devAlert('Please enter a valid email address.');
    }

    const parsedJobNumber = parseInt(jobCoaId, 10);
    const parsedNegotiatedPrice = parseMoneyToNumber(negotiatedPrice);
    const parsedCashAmount = parseMoneyToNumber(cashAmt);
    const parsedPaymentStatus = parseInt(paymentStatus, 10);
    const parsedJobStatus = parseInt(jobStatus, 10);

    if (!isNote && Number.isNaN(parsedJobNumber)) {
      return devAlert('Please select a valid job type.');
    }
    if (!isNote && negotiatedPriceError) {
      return devAlert(negotiatedPriceError);
    }
    if (Number.isNaN(parsedPaymentStatus) || Number.isNaN(parsedJobStatus)) {
      return devAlert('Please select valid status values.');
    }

    submitInFlightRef.current = true;
    setSavingEvent(true);
    try {
      let customerId = selectedCustomer ? selectedCustomer.cust_id : null;

      // If no customer selected, create one (skip for notes if not provided)
      if (!isNote && !customerId) {
        const custRes = await axios.post(`/customers`, {
          first_name: customerFirstName,
          last_name: customerLastName,
          email: customerEmail,
          phone_number: customerPhone,
          address: customerAddress1,
          city: customerCity,
          state: customerState,
          zip: customerZip
        }, { headers: { Authorization: `Bearer ${token}` } });
        customerId = custRes.data.cust_id;
      } else if (customerId && !isNote) {
        // Update existing customer details
        await axios.put(`/customers/${customerId}`, {
          first_name: customerFirstName,
          last_name: customerLastName,
          email: customerEmail,
          phone_number: customerPhone,
          address: customerAddress1,
          city: customerCity,
          state: customerState,
          zip: customerZip
        }, { headers: { Authorization: `Bearer ${token}` } });
      }

      const payload = {
        date: toLocalISOString(target),
        start_time: normalizeTime(startTime),
        end_time: normalizeTime(endTime),
        title,
        description,
        customer_id: isNote ? null : customerId,
        job_coa_id: isNote ? null : parsedJobNumber,
        job_number: isNote ? 0 : parsedJobNumber,
        negotiated_price: isNote ? 0 : parsedNegotiatedPrice,
        cash_amount: isNote ? 0 : (Number.isNaN(parsedCashAmount) ? 0 : parsedCashAmount),
        payment_status: isNote ? 2 : parsedPaymentStatus, // Default notes to Paid/Neutral
        job_status: parsedJobStatus,
        notes: notes || ''
      };

      let eventId;
      if (editingEventId) {
        await axios.put(`/events/${editingEventId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        eventId = editingEventId;
      } else {
        const res = await axios.post(`/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
        eventId = res.data.id;
      }

      // Send welcome email for new events with customer email (not for notes)
      if (!editingEventId && !isNote && customerEmail) {
        try {
          await axios.post(`/events/${eventId}/welcome-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
          console.log('Welcome email sent to customer');
        } catch (emailErr) {
          console.error('Failed to send welcome email:', emailErr);
          // Don't fail the event creation if email fails
        }
      }

      setShowForm(false);
      resetForm();
      await loadEvents();
      devAlert(editingEventId ? 'Event updated' : 'Event added');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save event';
      devAlert(msg);
    } finally {
      submitInFlightRef.current = false;
      setSavingEvent(false);
    }
  }

  // Simple add-note handler used by the lightweight modal
  async function handleAddNoteSave(data, eventId) {
    try {
      const payload = {
        date: data.date,
        start_time: data.start_time,
        end_time: data.end_time,
        title: data.title,
        description: data.description || '',
        customer_id: null,
        job_coa_id: null,
        job_number: 0,
        negotiated_price: 0,
        payment_status: 2,
        job_status: 5,
        notes: data.notes || ''
      };

      if (eventId) {
        await axios.put(`/events/${eventId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setNoteEditData(null);
        setShowAddNoteModal(false);
        await loadEvents();
        devAlert('Note updated');
      } else {
        // ensure title clearly indicates this is a Note
        if (payload.title && !/^\s*Note:/i.test(payload.title)) payload.title = `Note: ${payload.title}`;
        await axios.post(`/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setShowAddNoteModal(false);
        setMode('appt');
        try { localStorage.setItem('calendar_mode','appt'); } catch (e) {}
        showTemporaryToast('Switched to Appt');
        await loadEvents();
        devAlert('Note added');
      }
    } catch (err) {
      console.error('Failed to save note', err);
      devAlert(err?.response?.data?.error || 'Failed to save note');
    }
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <div className="left-controls">
          <button className="nav" onClick={prev} aria-label="Previous">‹</button>
        </div>

        <div className="center-controls">
          <div className="month-label">
            {view === 'month' && cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
            {view === 'week' && (() => {
              const s = startOfWeek(cursor);
              const e = addDays(s, 6);
              return `${s.toLocaleDateString()} − ${e.toLocaleDateString()}`;
            })()}
            {view === 'day' && cursor.toLocaleDateString()}
          </div>
          <div className="view-toggle">
            <button type="button" className={`view-btn ${view==='month'?'active':''}`} onClick={() => setView('month')}>Month</button>
            <button type="button" className={`view-btn ${view==='week'?'active':''}`} onClick={() => setView('week')}>Week</button>
            <button type="button" className={`view-btn ${view==='day'?'active':''}`} onClick={() => setView('day')}>Day</button>
            {canUseRouteOptimization && (
              <button
                type="button"
                className="view-btn"
                onClick={handleOptimizeRoute}
                disabled={routeLoading || routeCandidateCount < 2}
                title="Open optimized driving route in Google Maps"
              >
                {routeLoading ? 'Optimizing...' : 'Optimize Route'}
              </button>
            )}
          </div>
          {routeError && (
            <div style={{ color: '#b42318', fontSize: 12, marginTop: 6 }}>
              {routeError}
            </div>
          )}
          {showRouteSummary && (
            <div className="route-summary" style={{ marginTop: 8 }}>
              <div className="route-summary__header">
                <strong>Route Summary ({routeDateLabel})</strong>
                <span>
                  {formatRouteDistance(routeSummary.totalDistanceMeters)} · {formatRouteDuration(routeSummary.totalDurationSeconds)}
                </span>
              </div>
              <ol className="route-summary__list">
                {routeSummary.orderedStops.map((stop, idx) => (
                  <li key={`${stop.id || stop.address}-${idx}`}>
                    <span className="route-summary__stop-label">{stop.label || `Stop ${idx + 1}`}</span>
                    <span className="route-summary__stop-address">{stop.address}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div style={{display:'flex', gap:12, alignItems:'center', marginTop:6}}>
            <div className="mode-toggle" style={{display:'flex', gap:8, alignItems:'center'}}>
            <label style={{fontSize:12}}>
              <input
                type="radio"
                name="mode"
                value="appt"
                checked={mode==='appt'}
                style={{ accentColor: '#539c98' }}
                onChange={() => { setMode('appt'); try { localStorage.setItem('calendar_mode','appt'); } catch(e){} }}
              /> Appt
            </label>
            <label style={{fontSize:12}}>
              <input
                type="radio"
                name="mode"
                value="note"
                checked={mode==='note'}
                style={{ accentColor: '#539c98' }}
                onChange={() => { setMode('note'); try { localStorage.setItem('calendar_mode','note'); } catch(e){} }}
              /> Notes
            </label>
            </div>
            {isAdmin && (
              <div className="hours-toggle" style={{display:'flex', alignItems:'center'}}>
                <label style={{fontSize:12, display:'flex', gap:6, alignItems:'center'}}>
                  <input
                    type="checkbox"
                    checked={workingHoursOnly}
                    style={{ accentColor: '#539c98' }}
                    onChange={() => {
                      const next = !workingHoursOnly;
                      setWorkingHoursOnly(next);
                      try { localStorage.setItem('calendar_working_hours_only', JSON.stringify(next)); } catch (e) {}
                    }}
                  />
                  Working hours only
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="right-controls">
          <button className="nav" onClick={next} aria-label="Next">›</button>
        </div>
      </div>

      {showToast && <div className="ct-toast">{toastMsg}</div>}

      {view === 'month' && (
        <div className="calendar-grid month-view">
          <div className="calendar-weekday">Mon</div>
          <div className="calendar-weekday">Tue</div>
          <div className="calendar-weekday">Wed</div>
          <div className="calendar-weekday">Thu</div>
          <div className="calendar-weekday">Fri</div>

          {weeks.map((week, wi) => (
            <React.Fragment key={wi}>
              {week.map((dayObj, di) => {
                if (dayObj.getDay() === 0 || dayObj.getDay() === 6) return null;
                const inMonth = dayObj.getMonth() === cursor.getMonth();
                const isToday = isSameDay(dayObj, today);
                const dayStr = toLocalISOString(dayObj);
                const cellEvents = events
                  .filter(ev => ev.date === dayStr)
                  .filter(ev => !workingHoursOnly || eventOverlapsBusinessHours(ev))
                  .sort((a, b) => (a.start_time || '00:00').localeCompare(b.start_time || '00:00'));
                const isSelected = selectedDay && isSameDay(dayObj, selectedDay);
                return (
                  <div
                    key={di}
                    onClick={() => {
                      openCreateForDay(dayObj);
                    }}
                    onDoubleClick={() => { setCursor(new Date(dayObj)); setView('day'); }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, dayObj)}
                    className={`calendar-cell ${inMonth ? '' : 'muted'} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}>
                    <button
                      type="button"
                      className="cell-add-btn"
                      title="Add event on this date"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreateForDay(dayObj);
                      }}
                    >
                      +
                    </button>
                    <div className="date-number">{dayObj.getDate()}</div>
                    {cellEvents.length > 0 && (
                      <div className="cell-events">
                        {cellEvents.map(ev => (
                          <div
                            className={`cell-event status-${ev.job_status}`}
                            key={ev.id}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, ev)}
                            onClick={(e) => { e.stopPropagation(); handleEditEvent(ev); }}
                          >
                            <span className="line time">{formatTime(ev.start_time)} - {formatTime(ev.end_time)}</span>
                            <span className="line title">{renderEventTitle(ev)}</span>
                            <span className="line desc">{parseInt(ev.job_status, 10) === 5 ? (ev.notes || '') : (ev.description || '')}</span>
                            {/* Footer meta removed globally */}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}

      {view === 'week' && (
        <div className="calendar-grid week-view">
          <div className="calendar-weekday">Mon</div>
          <div className="calendar-weekday">Tue</div>
          <div className="calendar-weekday">Wed</div>
          <div className="calendar-weekday">Thu</div>
          <div className="calendar-weekday">Fri</div>

          {weekDays.map((d, i) => {
            if (d.getDay() === 0 || d.getDay() === 6) return null;
            const dayStr = toLocalISOString(d);
            const cellEvents = events
              .filter(ev => ev.date === dayStr)
              .filter(ev => !workingHoursOnly || eventOverlapsBusinessHours(ev))
              .sort((a, b) => (a.start_time || '00:00').localeCompare(b.start_time || '00:00'));
            const isSelectedWeek = selectedDay && isSameDay(d, selectedDay);
              return (
              <div 
                key={i} 
                onClick={() => {
                  openCreateForDay(d);
                }} 
                onDoubleClick={() => { setCursor(new Date(d)); setView('day'); }} 
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, d)}
                className={`calendar-cell ${d.getMonth()===cursor.getMonth()?'':'muted'} ${isSameDay(d,today)?'today':''} ${isSelectedWeek ? 'selected' : ''}`}
              >
                <button
                  type="button"
                  className="cell-add-btn"
                  title="Add event on this date"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateForDay(d);
                  }}
                >
                  +
                </button>
                <div className="date-number">{d.getDate()}</div>
                {cellEvents.length > 0 && (() => {
                  const useTimeLayout = cellEvents.some(ev => ev.start_time || ev.end_time);
                  return (
                    <div className={`cell-events ${useTimeLayout ? 'time-layout' : ''}`}>
                      {cellEvents.map(ev => {
                        if (useTimeLayout) {
                          const pos = computeEventPosition(ev);
                          return (
                            <div
                              className={`cell-event status-${ev.job_status}`}
                              key={ev.id}
                              draggable="true"
                              onDragStart={(e) => handleDragStart(e, ev)}
                              onClick={(e) => { e.stopPropagation(); handleEditEvent(ev); }}
                              style={{ top: pos.top, height: pos.height }}
                            >
                              <span className="time">{formatTime(ev.start_time)} - {formatTime(ev.end_time)}</span>
                              <span className="title"> {renderEventTitle(ev)}</span>
                            </div>
                          );
                        }
                        return (
                          <div
                            className={`cell-event status-${ev.job_status}`}
                            key={ev.id}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, ev)}
                            onClick={(e) => { e.stopPropagation(); handleEditEvent(ev); }}
                          >
                            <strong>{formatTime(ev.start_time)} - {formatTime(ev.end_time)}</strong> {renderEventTitle(ev)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {view === 'day' && (
        <div className="day-view">
          <div className={`day-header ${isSameDay(day,today)?'today':''}`}>{day.toLocaleDateString()}</div>
          <div className="day-body">
            <div>
              {token ? (
                <div>
                      {(() => {
                        const dayStr = toLocalISOString(day);
                        const todays = events
                          .filter(ev => ev.date === dayStr)
                          .filter(ev => !workingHoursOnly || eventOverlapsBusinessHours(ev))
                          .sort((a, b) => (a.start_time || '00:00').localeCompare(b.start_time || '00:00'));
                        if (todays.length === 0) return <p>No events yet for this day.</p>;

                        // If there are 3 or fewer events, center them vertically for focus.
                        if (todays.length <= 3) {
                          return (
                            <div className="day-events center" style={{minHeight: '220px'}}>
                              {todays.map(ev => (
                                <div key={ev.id} className={`cell-event status-${ev.job_status}`} style={{cursor:'pointer'}} onClick={(e)=>{ e.stopPropagation(); handleEditEvent(ev); }}>
                                  <strong>{formatTime(ev.start_time)} - {formatTime(ev.end_time)}</strong> — {renderEventTitle(ev)}{ev.description?` — ${ev.description}`:''}
                                </div>
                              ))}
                            </div>
                          );
                        }

                        // Otherwise use time-layout with absolute positioning and scrolling when needed
                        return (
                          <div className="day-events time-layout" style={{minHeight: '220px'}}>
                            {todays.map(ev => {
                              const pos = computeEventPosition(ev);
                              return (
                                <div
                                  key={ev.id}
                                  className={`cell-event status-${ev.job_status}`}
                                  style={{ position: 'absolute', left: 0, right: 0, top: pos.top, height: pos.height, cursor: 'pointer' }}
                                  onClick={(e)=>{ e.stopPropagation(); handleEditEvent(ev); }}
                                >
                                  <span className="time">{formatTime(ev.start_time)} - {formatTime(ev.end_time)}</span>
                                  <span className="title"> {renderEventTitle(ev)}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                  <div style={{marginTop:8}}>
                    <button type="button" onClick={() => { resetForm(); if (mode === 'note') setShowAddNoteModal(true); else setShowForm(true); }}>Add Event</button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>Please log in to see and add events.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={(ev) => { if (ev.target.classList.contains('modal-overlay')) setShowForm(false); }}>
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowForm(false)} aria-label="Close">✕</button>
            <form onSubmit={(e) => submitEvent(e)} className="event-form">
              <div>
                <label>{jobStatus === '5' ? 'Note Title' : 'Title (Auto-generated)'}</label><br />
                <input 
                  value={title} 
                  onChange={e => setTitle(e.target.value)}
                  readOnly={jobStatus !== '5'} 
                  style={{backgroundColor: jobStatus === '5' ? '#fff' : '#f0f0f0'}} 
                  placeholder="Enter note title..."
                />
              </div>
              <div style={{display: 'flex', gap: '10px'}}>
                <div>
                  <label>Start Time</label><br />
                  <select value={startTime} onChange={handleStartTimeChange} required>
                    {(workingHoursOnly ? getStartOptions(startTime) : TIME_OPTIONS).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>End Time</label><br />
                  <select value={endTime} onChange={e=>setEndTime(e.target.value)} required>
                    {(workingHoursOnly ? getEndOptions(startTime) : TIME_OPTIONS).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{display: 'flex', gap: '10px'}}>
                <div style={{flex: 2}}>
                  <label>Job Type</label><br />
                  <select 
                    value={jobCoaId ? String(jobCoaId) : ""} 
                    onChange={e => {
                      const selectedId = e.target.value;
                      const selectedCoa = jobCoas.find(coa => String(coa.coa_id) === selectedId);
                      setJobCoaId(selectedId);
                      setNegotiatedPrice(selectedCoa ? formatMoneyOnBlur(selectedCoa.price) : '');
                    }} 
                    required={jobStatus !== '5'}
                    disabled={jobStatus === '5'}
                    style={{width: '100%'}}
                  >
                    <option value="" disabled>Select a job type</option>
                    {jobCoas.length === 0 && <option disabled>No job types found</option>}
                    {jobCoas.map(coa => (
                      <option key={coa.coa_id} value={String(coa.coa_id)}>
                        {coa.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{flex: 1}}>
                  <label>Price</label><br />
                  <input 
                    type="text"
                    inputMode="decimal"
                    step="0.01"
                    value={negotiatedPrice} 
                    placeholder="0.00"
                    onMouseDown={handleMoneyMouseDown}
                    onFocus={e => e.target.select()}
                    onChange={e => setNegotiatedPrice(normalizeMoneyInput(e.target.value))}
                    onBlur={e => setNegotiatedPrice(formatMoneyOnBlur(e.target.value))}
                    required={jobStatus !== '5'}
                    disabled={jobStatus === '5'}
                    style={{width: '100%'}}
                  />
                  {jobStatus !== '5' && negotiatedPriceError && (
                    <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {negotiatedPriceError}
                    </div>
                  )}
                </div>
                <div style={{flex: 1}}>
                  <label style={{whiteSpace: 'nowrap'}}>Payment</label><br />
                  <select 
                    value={String(paymentStatus)} 
                    onChange={e => setPaymentStatus(e.target.value)} 
                    required={jobStatus !== '5'} 
                    disabled={jobStatus === '5'}
                    style={{width: '100%'}}
                  >
                    <option value="0">Unpaid</option>
                    <option value="2">Paid</option>
                  </select>
                </div>
                <div style={{flex: 1}}>
                  <label>Job Status</label><br />
                  <select 
                    value={String(jobStatus)} 
                    onChange={e => setJobStatus(e.target.value)} 
                    required 
                    style={{width: '100%'}}
                  >
                    <option value="0">Scheduled</option>
                    <option value="2">Completed</option>
                    <option value="3">Cancelled</option>
                    <option value="4">Allen</option>
                    <option value="6">Reschedule</option>
                  </select>
                </div>
              </div>
              {/* Customer Email removed from events; use customers table */}
              <div><label>Notes</label><br /><textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={4} /></div>

              {jobStatus !== '5' && (
                <>
                  <hr />
                  <h5>Customer Details</h5>
                  <div className="customer-search-box">
                    <label>Customer Search</label>
                    {selectedCustomer ? (
                      <div className="customer-selected">
                        <span>{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                        <button type="button" onClick={() => setSelectedCustomer(null)}>Change</button>
                      </div>
                    ) : (
                      <div>
                        <input 
                          type="text" 
                          placeholder="Search by name..." 
                          value={customerSearch} 
                          onChange={e => setCustomerSearch(e.target.value)} 
                        />
                        {customerResults.length > 0 && (
                          <div className="customer-results">
                            {customerResults.map(cust => (
                              <div 
                                key={cust.cust_id} 
                                className="customer-result-item"
                                onClick={() => {
                                  setSelectedCustomer(cust);
                                  setCustomerFirstName(cust.first_name || '');
                                  setCustomerLastName(cust.last_name || '');
                                  setCustomerEmail(cust.email || '');
                                  setCustomerPhone(formatPhoneInput(cust.phone_number || ''));
                                  setCustomerAddress1(cust.addr_street || '');
                                  setCustomerCity(cust.addr_city || '');
                                  setCustomerState(cust.addr_state || '');
                                  setCustomerZip(cust.addr_zip_code || '');
                                  setCustomerSearch('');
                                  setCustomerResults([]);
                                }}
                              >
                                {cust.first_name} {cust.last_name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <div style={{flex: 1}}><label>First Name</label><br /><input type="text" value={customerFirstName} onChange={e => setCustomerFirstName(e.target.value)} placeholder="First Name" style={{width: '100%'}} /></div>
                    <div style={{flex: 1}}><label>Last Name</label><br /><input type="text" value={customerLastName} onChange={e => setCustomerLastName(e.target.value)} placeholder="Last Name" style={{width: '100%'}} /></div>
                    <div style={{flex: 1}}><label>Email</label><br /><input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="customer@example.com" style={{width: '100%'}} /></div>
                    <div style={{flex: 1}}><label>Phone</label><br /><input type="tel" value={customerPhone} onChange={e => setCustomerPhone(formatPhoneInput(e.target.value))} placeholder="(555) 123-4567" maxLength={14} style={{width: '100%'}} /></div>
                  </div>
                  <div style={{display: 'flex', gap: '20px', marginTop: '10px'}}>
                    <div style={{flex: 1}}>
                      <div style={{marginBottom: '10px'}}>
                        <label>Address</label><br />
                        <input type="text" value={customerAddress1} onChange={e => setCustomerAddress1(e.target.value)} placeholder="123 Main St" style={{width: '100%'}} />
                      </div>
                      <div style={{display: 'flex', gap: '10px'}}>
                        <div style={{flex: 2}}><label>City</label><br /><input type="text" value={customerCity} onChange={e => setCustomerCity(e.target.value)} style={{width: '100%'}} /></div>
                        <div style={{flex: 0.5}}><label>State</label><br /><input type="text" value={customerState} onChange={e => setCustomerState(e.target.value)} style={{width: '100%'}} /></div>
                        <div style={{flex: 1}}><label style={{whiteSpace: 'nowrap'}}>ZIP Code</label><br /><input type="text" value={customerZip} onChange={e => setCustomerZip(e.target.value)} style={{width: '100%'}} /></div>
                      </div>
                    </div>
                    <div style={{flex: 1, height: '150px', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd'}}>
                      {(customerAddress1 || customerCity) ? (
                        <iframe
                          title="Location Map"
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{border:0}}
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(`${customerAddress1} ${customerCity} ${customerState} ${customerZip}`)}&output=embed`}
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', background: '#f9f9f9'}}>
                          Map Preview
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {editingEventId && canUseAttachments && (
                <>
                  <hr />
                  {(() => {
                    const hasAttachments = attachments.length > 0;
                    const showSection = attachmentsLoading || hasAttachments || attachmentUploading || Boolean(attachmentFile) || showAttachmentComposer;

                    if (!showSection && canEditAttachments) {
                      return (
                        <div>
                          <button type="button" onClick={() => setShowAttachmentComposer(true)}>
                            Add Attachment
                          </button>
                        </div>
                      );
                    }

                    if (!showSection) {
                      return (
                        <div className="event-attachments">
                          <div className="event-attachments__header-row">
                            <h5 className="event-attachments__title">Attachments</h5>
                            <span className="event-attachments__count">0</span>
                          </div>
                          <div className="event-attachments__hint">
                            No attachments yet. Upload is restricted to office users.
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="event-attachments">
                        <div className="event-attachments__header-row">
                          <h5 className="event-attachments__title">Attachments</h5>
                          <span className="event-attachments__count">{attachments.length}</span>
                        </div>
                        {canEditAttachments ? (
                          <div className="event-attachments__upload-row">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.docx,.xlsx,.txt"
                              className="event-attachments__file-input"
                              onChange={e => setAttachmentFile(e.target.files?.[0] || null)}
                              disabled={attachmentUploading}
                            />
                            <button
                              type="button"
                              onClick={handleUploadAttachment}
                              disabled={!attachmentFile || attachmentUploading}
                            >
                              {attachmentUploading ? 'Uploading...' : 'Upload'}
                            </button>
                            <span className="event-attachments__hint">
                              Allowed: photos + pdf/docx/xlsx/txt (10MB max)
                            </span>
                          </div>
                        ) : (
                          <div className="event-attachments__hint">
                            View-only attachments for this role.
                          </div>
                        )}
                        {attachmentError && (
                          <div className="event-attachments__error">
                            {attachmentError}
                          </div>
                        )}
                        <div className="event-attachments__list-wrap">
                          {attachmentsLoading ? (
                            <div className="event-attachments__empty">Loading attachments...</div>
                          ) : attachments.length === 0 ? (
                            <div className="event-attachments__empty">No attachments yet.</div>
                          ) : (
                            <div className="event-attachments__list">
                              {attachments.map(att => (
                                <div key={att.id} className="event-attachments__item">
                                  <div className="event-attachments__item-row">
                                    <div className="event-attachments__meta">
                                      <span className="event-attachments__badge">{getAttachmentTypeLabel(att)}</span>
                                      <div className="event-attachments__name" title={att.original_name}>
                                        {att.original_name}
                                      </div>
                                      <div className="event-attachments__size">
                                        {formatFileSize(att.size_bytes)}
                                      </div>
                                    </div>
                                    <div className="event-attachments__actions">
                                      {isImageAttachment(att) && (
                                        <button
                                          type="button"
                                          onClick={() => handleToggleAttachmentPreview(att)}
                                          disabled={Boolean(attachmentPreviewLoading[att.id])}
                                        >
                                          {attachmentPreviewLoading[att.id]
                                            ? 'Previewing...'
                                            : (attachmentPreviews[att.id] ? 'Hide Preview' : 'Preview')}
                                        </button>
                                      )}
                                      <button type="button" onClick={() => handleDownloadAttachment(att)}>Download</button>
                                      {canEditAttachments && (
                                        <button type="button" onClick={() => handleDeleteAttachment(att.id)}>Delete</button>
                                      )}
                                    </div>
                                  </div>
                                  {attachmentPreviews[att.id] && isImageAttachment(att) && (
                                    <div className="event-attachments__preview-wrap">
                                      <img
                                        src={attachmentPreviews[att.id]}
                                        alt={att.original_name || 'Attachment preview'}
                                        className="event-attachments__preview"
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              <hr />
              <div style={{marginTop:8, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                  <button type="submit" disabled={savingEvent || (jobStatus !== '5' && !!negotiatedPriceError)}>{savingEvent ? 'Saving...' : 'Save'}</button>
                  <button type="button" onClick={() => setShowForm(false)} style={{marginLeft:8}} disabled={savingEvent || deletingEvent}>Cancel</button>
                  {editingEventId && (
                    <button
                      type="button"
                      onClick={handleDeleteEvent}
                      style={{marginLeft:8, background:'#b91c1c', color:'#fff', border:'1px solid #991b1b'}}
                      disabled={savingEvent || deletingEvent}
                    >
                      {deletingEvent ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                  {deleteEventError && (
                    <div style={{ marginTop: 8, color: '#b42318', maxWidth: 560, fontWeight: 600 }}>
                      {deleteEventError}
                    </div>
                  )}
                </div>
                {editingEventId && parseInt(paymentStatus, 10) !== 2 && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <div>
                      <label htmlFor="cash-amt" style={{fontSize: '0.9em', marginRight: '5px'}}>Cash Amt</label>
                      <input
                        id="cash-amt"
                        type="text"
                        inputMode="decimal"
                        step="0.01"
                        value={cashAmt}
                        placeholder="0.00"
                        onMouseDown={handleMoneyMouseDown}
                        onFocus={e => e.target.select()}
                        onChange={e => {
                          const normalized = normalizeMoneyInput(e.target.value);
                          if (normalized === '') {
                            setCashAmt('');
                            return;
                          }

                          const val = parseFloat(normalized);
                          const total = parseFloat(negotiatedPrice) || 0;

                          if (!Number.isNaN(val) && val > total) {
                            setCashAmt(total.toFixed(2));
                          } else {
                            setCashAmt(normalized);
                          }
                        }}
                        onBlur={e => {
                          setCashAmt(formatMoneyOnBlur(e.target.value));
                        }}
                        style={{width: '100px'}}
                      />
                    </div>
                    <button 
                      type="button" 
                      onClick={handleSendInvoice}
                      style={{background: '#28a745', color: 'white', border: 'none'}}
                    >
                      Send Invoice ✉️
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightweight notes modal used when mode === 'note' */}
      <AddNoteModal 
        open={showAddNoteModal} 
        onClose={() => { setShowAddNoteModal(false); setNoteEditData(null); }} 
        defaultDate={selectedDay} 
        initialData={noteEditData}
        onSave={handleAddNoteSave} 
      />
    </div>
  );
}

export default Calendar;
